import React, { useEffect, useState, useCallback } from "react";
import { Tooltip } from "antd";
import { ClockCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

interface SlaCountdownProps {
  targetDateTime: string | null;
  className?: string;
}

type UrgencyLevel = "safe" | "warning" | "urgent" | "overdue";

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  totalMilliseconds: number;
  isOverdue: boolean;
}

const URGENCY_STYLES: Record<UrgencyLevel, { bg: string; text: string; border: string }> = {
  safe: {
    bg: "bg-green-100",
    text: "text-green-700",
    border: "border-green-300",
  },
  warning: {
    bg: "bg-yellow-100",
    text: "text-yellow-700",
    border: "border-yellow-300",
  },
  urgent: {
    bg: "bg-red-100",
    text: "text-red-700",
    border: "border-red-300",
  },
  overdue: {
    bg: "bg-red-200",
    text: "text-red-800",
    border: "border-red-400",
  },
};

const UPDATE_INTERVAL_MS = 30000; // 30 seconds

function calculateTimeRemaining(targetDateTime: string): TimeRemaining {
  const now = new Date();
  const target = new Date(targetDateTime);
  const diff = target.getTime() - now.getTime();

  const isOverdue = diff < 0;
  const absDiff = Math.abs(diff);

  const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((absDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));

  return {
    days,
    hours,
    minutes,
    totalMilliseconds: diff,
    isOverdue,
  };
}

function getUrgencyLevel(totalMilliseconds: number): UrgencyLevel {
  if (totalMilliseconds < 0) {
    return "overdue";
  }

  const hoursRemaining = totalMilliseconds / (1000 * 60 * 60);

  if (hoursRemaining < 1) {
    return "urgent";
  }

  if (hoursRemaining < 2) {
    return "warning";
  }

  return "safe";
}

function formatTimeRemaining(time: TimeRemaining): string {
  const { days, hours, minutes, isOverdue } = time;

  let timeString: string;

  if (days > 0) {
    timeString = `${days}d ${hours}h`;
  } else {
    timeString = `${hours}h ${minutes}m`;
  }

  if (isOverdue) {
    return `Vencido hace ${timeString}`;
  }

  return `Faltan ${timeString}`;
}

function getTooltipContent(targetDateTime: string, time: TimeRemaining): string {
  const targetDate = new Date(targetDateTime);
  const formattedDate = targetDate.toLocaleString("es-MX", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  if (time.isOverdue) {
    return `Fecha límite: ${formattedDate} (vencido)`;
  }

  return `Fecha límite: ${formattedDate}`;
}

function getAccessibilityLabel(time: TimeRemaining, urgencyLevel: UrgencyLevel): string {
  const timeText = formatTimeRemaining(time);

  const urgencyLabels: Record<UrgencyLevel, string> = {
    safe: "",
    warning: "Atención: ",
    urgent: "Urgente: ",
    overdue: "Vencido: ",
  };

  return `${urgencyLabels[urgencyLevel]}${timeText}`;
}

export const SlaCountdown: React.FC<SlaCountdownProps> = ({
  targetDateTime,
  className = "",
}) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(null);

  const updateTime = useCallback(() => {
    if (targetDateTime) {
      setTimeRemaining(calculateTimeRemaining(targetDateTime));
    }
  }, [targetDateTime]);

  useEffect(() => {
    if (!targetDateTime) {
      setTimeRemaining(null);
      return;
    }

    updateTime();

    const interval = setInterval(updateTime, UPDATE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [targetDateTime, updateTime]);

  if (!targetDateTime || !timeRemaining) {
    return null;
  }

  const urgencyLevel = getUrgencyLevel(timeRemaining.totalMilliseconds);
  const styles = URGENCY_STYLES[urgencyLevel];
  const displayText = formatTimeRemaining(timeRemaining);
  const tooltipContent = getTooltipContent(targetDateTime, timeRemaining);
  const ariaLabel = getAccessibilityLabel(timeRemaining, urgencyLevel);

  const isOverdue = urgencyLevel === "overdue";
  const Icon = isOverdue ? ExclamationCircleOutlined : ClockCircleOutlined;

  return (
    <Tooltip title={tooltipContent} placement="top">
      <span
        className={`
          inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium
          border ${styles.bg} ${styles.text} ${styles.border}
          ${isOverdue ? "animate-pulse" : ""}
          ${className}
        `}
        role="status"
        aria-label={ariaLabel}
      >
        <Icon className="text-xs" aria-hidden="true" />
        <span>{displayText}</span>
        {urgencyLevel === "urgent" && !isOverdue && (
          <span className="sr-only">Urgente</span>
        )}
      </span>
    </Tooltip>
  );
};

export default SlaCountdown;
